// Prefetch dos chunks das páginas do site (storefront).
//
// Objetivo: navegação instantânea. O chunk da página é baixado antes do clique
// (no hover/toque do link, ou em tempo ocioso do navegador), então ao clicar o
// React já tem o módulo em memória e não precisa exibir tela de carregando.
//
// Os factories abaixo são os mesmos `import()` usados no lazy() do App.tsx, então
// o Vite/Rollup deduplica — nenhum chunk extra é criado.

import { prefersReducedData } from "@/lib/networkHints";

type Loader = () => Promise<unknown>;

/** Rotas fixas do site → loader do chunk. */
const staticRoutes: Record<string, Loader> = {
  "/": () => import("@/pages/Index"),
  "/colecao": () => import("@/pages/Vitrine"),
  "/lancamentos": () => import("@/pages/Vitrine"),
  "/promocoes": () => import("@/pages/Vitrine"),
  "/categorias": () => import("@/pages/Categorias"),
  "/videos": () => import("@/pages/Videos"),
  "/looks": () => import("@/pages/Looks"),
  "/pesquisar": () => import("@/pages/Busca"),
  "/checkout": () => import("@/pages/Checkout"),
  "/entrar": () => import("@/pages/Auth"),
  "/meus-pedidos": () => import("@/pages/MeusPedidos"),
  "/rastreio": () => import("@/pages/Rastreio"),
  "/acompanhar": () => import("@/pages/AcompanharPedido"),
  "/feedback": () => import("@/pages/Feedback"),
  "/promocao-24h": () => import("@/pages/Promocao24h"),
  "/grupos": () => import("@/pages/Grupos"),
};

/** Rotas com parâmetro → prefixo do pathname. */
const prefixRoutes: Array<[string, Loader]> = [
  ["/produto/", () => import("@/pages/ProdutoPage")],
  ["/categoria/", () => import("@/pages/Vitrine")],
  ["/colecao/", () => import("@/pages/Vitrine")],
  ["/pedido/", () => import("@/pages/PedidoConfirmado")],
  ["/rastreio/", () => import("@/pages/RastreioResultado")],
  ["/termos-de-uso", () => import("@/pages/Legal")],
  ["/politica-", () => import("@/pages/Legal")],
];

function resolveLoader(pathname: string): Loader | undefined {
  const direct = staticRoutes[pathname];
  if (direct) return direct;
  const match = prefixRoutes.find(([prefix]) => pathname.startsWith(prefix));
  return match?.[1];
}

const done = new Set<string>();

/** Caminho em que a visita começou (antes de qualquer navegação interna). */
const entryPath = typeof window !== "undefined" ? window.location.pathname : "";
/** Entrada direta num produto: nada ocioso disputa a 1ª foto; carrinho/rotas só por intenção. */
export const isDirectProductEntry = (path = entryPath) => path.startsWith("/produto/");

/** Baixa o chunk de uma rota do site (idempotente). Resolve quando o chunk termina. */
export function prefetchSiteRoute(href: string): Promise<void> {
  if (!href || href.startsWith("/admin")) return Promise.resolve();
  let pathname = href;
  try {
    pathname = new URL(href, window.location.origin).pathname;
  } catch {
    return Promise.resolve();
  }
  if (done.has(pathname)) return Promise.resolve();
  const loader = resolveLoader(pathname);
  if (!loader) return Promise.resolve();
  done.add(pathname);
  return loader().then(() => undefined, () => { done.delete(pathname); });
}

/** Rede móvel lenta (3G ou pior): aquecimento ocioso desligado, só intenção. */
function slowNetwork(): boolean {
  const c = typeof navigator === "undefined" ? undefined : (navigator as any).connection;
  return prefersReducedData() || /(^|-)?3g$/.test(c?.effectiveType || "");
}

const idle: (cb: () => void) => void =
  typeof window !== "undefined" && "requestIdleCallback" in window
    ? (cb) => (window as any).requestIdleCallback(cb, { timeout: 2500 })
    : (cb) => setTimeout(cb, 300);

/** Executa depois do evento load da página (nunca disputa a primeira visita). */
function afterLoad(cb: () => void) {
  if (typeof document === "undefined") return;
  if (document.readyState === "complete") cb();
  else window.addEventListener("load", cb, { once: true });
}

/**
 * Prefetch em fila, um por vez: começa só depois do load, espera o chunk
 * anterior terminar e um novo momento ocioso antes do próximo.
 */
export function prefetchIdle(loaders: Array<() => Promise<unknown>>): void {
  if (slowNetwork()) return;
  let i = 0;
  const next = () => {
    if (i >= loaders.length) return;
    loaders[i++]().catch(() => undefined).then(() => idle(next));
  };
  afterLoad(() => idle(next));
}

export function prefetchSiteRoutesIdle(paths: string[]): void {
  prefetchIdle(paths.filter((p) => !done.has(p)).map((p) => () => prefetchSiteRoute(p)));
}

/**
 * Escuta global: qualquer link interno do site passa a ser prefetchado
 * no hover (desktop) ou no primeiro toque (mobile), cobrindo header,
 * barra fixa, menus, carrosséis da home e cards de produto.
 */
let activeListeners = 0;
let removeListeners: (() => void) | null = null;

/** Registra a escuta (idempotente) e devolve a função de limpeza. */
export function initSiteLinkPrefetch(): () => void {
  if (typeof document === "undefined") return () => {};
  activeListeners += 1;
  if (!removeListeners) {
    const fromEvent = (e: Event) => {
      // Em economia de dados, só prefetch no toque/foco (intenção forte), não no passar do mouse.
      if (prefersReducedData() && e.type === "pointerover") return;
      const target = e.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/")) return;
      prefetchSiteRoute(href);
    };
    const opts = { capture: true, passive: true } as AddEventListenerOptions;
    const events = ["pointerover", "touchstart", "focusin"] as const;
    events.forEach((ev) => document.addEventListener(ev, fromEvent, opts));
    removeListeners = () => events.forEach((ev) => document.removeEventListener(ev, fromEvent, opts));
  }
  return () => {
    activeListeners = Math.max(0, activeListeners - 1);
    if (activeListeners === 0 && removeListeners) {
      removeListeners();
      removeListeners = null;
    }
  };
}

/** Rotas mais usadas do storefront, aquecidas em tempo ocioso (o resto vai por intenção). */
// Checkout não entra: só por intenção (toque/foco no link), nunca na primeira visita.
export const CORE_SITE_ROUTES = [
  "/colecao",
];
